import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../u2000Core/axios/U2000UseAxiosAuthenticated';
import { U2000HandleApiError } from '../u2000Core/axios/U2000HandleApiError';
import type { U2000SearchPayload } from '../u2000Core/components/tables/basic/U2000BasicTable';
import type { U2000CriteriaLookupConfig } from '../u2000Core/components/tables/basic/U2000BasicTableList';
import { useToast } from '../u2000Core/context/toast/U2000ToastContext';
import { NS } from './U3125';
import { doSearch, luHandleAppiError, u3125FormatInvoiceNumberDisplay, type U3125InvoiceSearchRow } from './U3125Service';

export type U3125FindInvoiceSearchCriterias = { fieldName: string; value: unknown }[];

/** Autocomplétion n° facture via POST /U3125/search (pas de find* dédié). */
export function useU3125FindInvoiceHandlers() {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const search = useCallback(
    async (criterias: U3125FindInvoiceSearchCriterias): Promise<U3125InvoiceSearchRow[]> => {
      try {
        const pageSizeRaw = criterias.find((c) => c.fieldName === 'pageSize')?.value;
        const pageSize = Math.min(Math.max(Number(pageSizeRaw) || 15, 1), 50);
        const invoiceNumberVal = criterias.find((c) => c.fieldName === 'invoiceNumber')?.value;
        const query = typeof invoiceNumberVal === 'string' ? invoiceNumberVal.trim() : '';
        if (!query) {
          return [];
        }

        const searchPayload: U2000SearchPayload = {
          filter: {
            criterias: [{ field: 'invoiceNumber', operator: 'startswith', value: query, type: 'string' }],
            fields: [{ name: 'invoiceNumber' }],
            sortedFields: [{ name: 'invoiceNumber', sortOrder: 'DESC' }],
          },
          pagination: { page: 1, size: pageSize },
        };

        const response = await doSearch(axios, searchPayload);
        const results = response.data?.results ?? [];
        return Array.isArray(results) ? (results as U3125InvoiceSearchRow[]) : [];
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return [];
      }
    },
    [axios, t, toast],
  );

  return { search };
}

export function u3125InvoiceLookupItemToValue(item: unknown, _config: U2000CriteriaLookupConfig): string {
  const row = item as U3125InvoiceSearchRow;
  if (row?.invoiceNumber == null) {
    return '';
  }
  return String(row.invoiceNumber).trim();
}

export function u3125InvoiceLookupItemToDisplay(item: unknown, _config: U2000CriteriaLookupConfig): string {
  const row = item as U3125InvoiceSearchRow;
  const num = u3125FormatInvoiceNumberDisplay(row?.invoiceNumber ?? null);
  const p = row?.project;
  const proj = p ? [p.projetNumber, p.projetName].filter((v) => v != null && String(v).trim() !== '').join(' — ') : '';
  return proj ? `${num} — ${proj}` : num;
}

export function u3125InvoiceLookupItemToSelectedDisplay(item: unknown, _config: U2000CriteriaLookupConfig): string {
  const row = item as U3125InvoiceSearchRow;
  return u3125FormatInvoiceNumberDisplay(row?.invoiceNumber ?? null);
}
